import React from 'react';
import { ContextMenuComplete, ContextMenuItemType } from '@marageti/z4-lib';
import {
  Typography, Theme, Skeleton, Button, Stack, useMediaQuery,
} from '@mui/material';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import AnnouncementBar from '../announcement-bar';
import SelectTeamMemberDialog from './select-team-member-dialog';

type AssignedToTextProps = {
  isLoading: boolean;
  isUnassigned: boolean;
  isAssignedToSelf: boolean;
  assignedToName: string | undefined;
};

const AssignedToText = ({
  isLoading,
  isUnassigned,
  isAssignedToSelf,
  assignedToName,
}: AssignedToTextProps) => {
  if (isLoading) {
    return <Typography variant="caption"><Skeleton width={100} /></Typography>;
  }

  if (isUnassigned) {
    return (
      <Typography variant="caption" color="primary">
        Assign this request for sending a message or matching with a partner.
      </Typography>
    );
  }

  return (
    <Typography variant="caption" color="primary">
      {isAssignedToSelf ? 'Assigned to You' : (
        <>
          Assigned to
          {' '}
          <strong>{assignedToName}</strong>
        </>
      )}
    </Typography>
  );
};

type TripRequestAssignToBarViewProps = {
  isLoading: boolean;
  isUnassigned: boolean;
  isAssignedToSelf: boolean;
  assignedToName?: string;
  onAssignToSelf: () => void;
  onAssignToTeamMember: (teamMember: TeamMember) => void;
  onUnassign: () => void;
  teamMembers: TeamMember[];
};

const TripRequestAssignToBarView = ({
  isLoading,
  isAssignedToSelf,
  isUnassigned,
  assignedToName = undefined,
  onAssignToSelf,
  onAssignToTeamMember,
  onUnassign,
  teamMembers,
}: TripRequestAssignToBarViewProps) => {
  const [showSelectTeamMemberDialog, setShowSelectTeamMemberDialog] = React.useState(false);

  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  const contextMenuItems: ContextMenuItemType[] = [
    (!isUnassigned && (
      {
        action: onUnassign,
        label: 'Unassign',
      }
    )),
    (isMobile && {
      action: onAssignToSelf,
      label: 'Assign to Me',
    }),
    {
      action: () => setShowSelectTeamMemberDialog(true),
      label: 'Assign to...',
    },
  ]
    .filter(Boolean) as ContextMenuItemType[];

  return (
    <>
      <AnnouncementBar
        endAdornment={(
          <Stack direction="row" spacing={1} alignItems="center" sx={{ marginRight: -1.5 }}>
            {isUnassigned && !isMobile && (
              <Button
                variant="outlined"
                size="small"
                onClick={onAssignToSelf}
                sx={{
                  py: 1,
                  px: 1.5,
                }}
              >
                <Typography variant="captionBold" color="primary" noWrap>Assign to Me</Typography>
              </Button>
            )}
            <ContextMenuComplete
              id="assign-to-menu"
              items={contextMenuItems}
              contextMenuButtonProps={{
                edge: 'end',
              }}
            />
          </Stack>
        )}
        sx={{
          background: (theme: Theme) => {
            if (!assignedToName) {
              return theme.palette.common.warning10;
            }
            if (isAssignedToSelf) {
              return theme.palette.common.green25;
            }
            return theme.palette.common.gold25;
          },
          boxShadow: 'none',
        }}
      >
        <AssignedToText
          isLoading={isLoading}
          isUnassigned={isUnassigned}
          isAssignedToSelf={isAssignedToSelf}
          assignedToName={assignedToName}
        />
      </AnnouncementBar>
      <SelectTeamMemberDialog
        title={isUnassigned ? 'Assign to...' : 'Reassign'}
        open={showSelectTeamMemberDialog}
        onClose={() => setShowSelectTeamMemberDialog(false)}
        teamMembers={teamMembers}
        onSelectTeamMember={onAssignToTeamMember}
      />
    </>
  );
};

export default TripRequestAssignToBarView;
